/**
 * PrintableSpecsTab — section "Catalogue POD" de la page Réglages
 * (refonte typée PR2).
 *
 * CRUD sur `printable_specs` typés (domaine C) :
 *   - 1 bouton "+ Nouvelle config" header ouvre le wizard, qui
 *     démarre sur un step 0 picker (Papier / Cadrée). Action unique
 *     en surface — pas de doublon header/EmptyState.
 *   - Plus de bouton "Modifier" : les codes typés sont immuables
 *     post-création (l'identité `attrs_key` UNIQUE en dépend). Pour
 *     changer un attribut, il faut archiver et recréer.
 *   - Plus de bouton "Re-Quote" : le Quote sync est interne au create
 *     atomique. Diagnostic ponctuel = curl admin direct.
 *
 * Refonte tarifs 2026-05-15 : la sous-section repliable "Pricing par défaut
 * atelier" (qui imbriquait `<PodPricingTab>`) a été retirée. Le pricing
 * vit désormais dans le tab dédié "Tarification" (slug `pricing`).
 */

import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Pagination from '@/components/ui/Pagination'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useDeletePrintableSpec, usePrintableSpecs } from '@/hooks/usePrintableSpecs'
import type { PrintableSpecResponse } from '@/utils/api/printable-specs'

import EtsyFreeShippingZonesSelector from './EtsyFreeShippingZonesSelector'
import EtsyReadinessStateSelector from './EtsyReadinessStateSelector'
import EtsyReturnPolicySelector from './EtsyReturnPolicySelector'
import EtsyShippingProfileSelector from './EtsyShippingProfileSelector'
import PodDimensionsWhitelistSelector from './PodDimensionsWhitelistSelector'
import PodFrameColorsWhitelistSelector from './PodFrameColorsWhitelistSelector'
import PodMatrixDefaultsSelector from './PodMatrixDefaultsSelector'
import PrintableSpecModal from './PrintableSpecModal'

import './EtsyShippingProfileSelector.css'
import './PrintableSpecsTab.css'

const PROVIDER_STATUS_FR: Record<PrintableSpecResponse['provider_status'], string> = {
  active: 'Actif',
  deprecated: 'Déprécié',
  retired: 'Retiré',
}

function formatAttributes(attrs: Record<string, unknown>): string {
  const entries = Object.entries(attrs)
  if (entries.length === 0) return '(aucun attribut)'
  return entries.map(([k, v]) => `${k}=${String(v)}`).join(' · ')
}

export default function PrintableSpecsTab() {
  const { confirm } = useConfirm()

  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)

  const listQuery = usePrintableSpecs({ page, page_size: 50 })
  const deleteMutation = useDeletePrintableSpec()

  const handleDelete = async (row: PrintableSpecResponse) => {
    const ok = await confirm({
      title: 'Supprimer définitivement cette config ?',
      message: `« ${row.label_cache} » et ses ${row.variants.length} variante(s) seront supprimées définitivement. Les listings Etsy déjà publiés gardent leur snapshot figé et continuent à fonctionner pour le routage POD (forensique préservée).`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onSuccess: () => {
        toast.success(`« ${row.label_cache} » supprimée`)
      },
      onError: (err) => {
        const detailObj = err.detail as { message?: string } | string | undefined
        const detail =
          typeof detailObj === 'string'
            ? detailObj
            : (detailObj?.message ?? 'Suppression impossible')
        toast.error(detail)
      },
    })
  }

  const rows = listQuery.data?.items ?? []
  const isLoading = listQuery.isLoading

  return (
    <section className="printable-specs-tab">
      <header className="printable-specs-tab-header">
        <div>
          <h2 className="ui-h4 printable-specs-tab-title">Catalogue POD · Gelato</h2>
          <p className="ui-body ui-text-muted printable-specs-tab-subtitle">
            Configurations production validées chez Gelato. Chaque ligne est une combinaison typée
            (papier + couleur + cadre…) que tu acceptes de vendre — les variantes d'œuvres viennent
            s'y binder.
          </p>
        </div>
        <div className="printable-specs-tab-actions">
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" size={16} />
            Nouvelle config
          </Button>
        </div>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucune config production"
          description="Crée ta première spec depuis le bouton « Nouvelle config » en haut à droite. Les variantes par taille × couleur sont générées automatiquement depuis le catalogue Gelato."
        />
      ) : (
        <ul className="printable-specs-list">
          {rows.map((row) => (
            <li key={row.id} className="printable-specs-list-item">
              <details className="printable-specs-row">
                <summary className="printable-specs-row-summary">
                  <div className="printable-specs-row-header">
                    <strong>{row.label_cache}</strong>
                    <div className="printable-specs-row-badges">
                      <Badge size="sm">
                        {row.variants.length} variante{row.variants.length > 1 ? 's' : ''}
                      </Badge>
                      <Badge variant={row.provider_status === 'active' ? 'success' : 'default'}>
                        {PROVIDER_STATUS_FR[row.provider_status]}
                      </Badge>
                    </div>
                  </div>
                  <div className="printable-specs-row-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_cache}`}
                      items={[
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </div>
                </summary>
                <div className="printable-specs-row-body">
                  <div className="printable-specs-row-attrs">
                    <Badge size="sm">
                      <code className="printable-specs-sku">{row.sku}</code>
                    </Badge>
                    <Badge size="sm">{formatAttributes(row.attributes)}</Badge>
                  </div>
                  {row.variants.length === 0 ? (
                    <p className="ui-body ui-text-muted">
                      Aucun variant — ajoute une taille via l&apos;API admin.
                    </p>
                  ) : (
                    <table className="printable-specs-variants-table">
                      <thead>
                        <tr>
                          <th>SKU (productUid)</th>
                          <th>Dimensions</th>
                          <th>Coût Gelato</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.variants.map((v) => (
                          <tr key={v.id}>
                            <td>
                              <code className="printable-specs-sku">{v.sku}</code>
                            </td>
                            <td>
                              {v.width_cm}×{v.height_cm} cm
                            </td>
                            <td className="printable-specs-table-numeric">
                              {v.base_cost_cents_eur != null ? (
                                `${(v.base_cost_cents_eur / 100).toFixed(2)} €`
                              ) : (
                                <span className="ui-text-muted">à quoter</span>
                              )}
                            </td>
                            <td>
                              <Badge
                                variant={v.provider_status === 'active' ? 'success' : 'default'}
                                size="sm"
                              >
                                {PROVIDER_STATUS_FR[v.provider_status]}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </details>
            </li>
          ))}
        </ul>
      )}

      {listQuery.data && (
        <Pagination
          page={listQuery.data.page}
          pageSize={listQuery.data.page_size}
          total={listQuery.data.total}
          hasNext={listQuery.data.has_next}
          onPageChange={setPage}
        />
      )}

      {/* Refonte tarifs 2026-05-15 — la section repliable "Pricing par défaut
          atelier" qui imbriquait <PodPricingTab> ici a été retirée. La
          configuration margin POD + frais Etsy + digital default vit
          désormais dans le tab dédié "Tarification" (slug `pricing`). */}

      {/* ──────────────────────────────────────────────────────────────────
          2026-05-16 — section "Paramètres publication Etsy".
          Les 5 blocs Etsy (shipping profile / readiness / return policy /
          frame colors priority / free shipping zones) ont migré ici depuis
          PricingTab : c'est leur domaine fonctionnel (Catalogue POD), pas
          calcul de prix. La whitelist dimensions POD (Option A —
          curation source de vérité pour le dropdown variantes) vit aussi
          ici car elle pilote ce que le catalogue accepte d'offrir.
          ────────────────────────────────────────────────────────────── */}
      <section className="printable-specs-etsy-section">
        <header className="printable-specs-tab-header">
          <div>
            <h2 className="ui-h4 printable-specs-tab-title">Paramètres publication Etsy</h2>
            <p className="ui-body ui-text-muted printable-specs-tab-subtitle">
              Defaults appliqués à chaque publication d&apos;une variante POD sur Etsy : profile
              expédition atelier (limited/original), délai de fabrication, politique de retour,
              priorité des couleurs de cadre (cap 70 variantes), pays free shipping baked, et
              whitelist des dimensions proposées au catalogue.
            </p>
          </div>
        </header>

        {/* Whitelist dimensions = catalog config (multi-select grille
            qui scale 1→N colonnes selon la largeur disponible). Vit
            en pleine largeur du tab, hors de la grille 2-cols qui suit. */}
        <PodDimensionsWhitelistSelector />

        {/* Plan B Palier 3 2026-05-18 — whitelist FrameColor Gelato +
            self-service provisioning : au save, auto-création des
            framings + specs + mapping atelier en lot. */}
        <PodFrameColorsWhitelistSelector />

        {/* Plan B 2026-05-18 — mapping atelier `{finish_key: spec_id}`
            consommé par `auto_create_for_work` pour router chaque
            finition d'une nouvelle matrice vers son productUid Gelato
            dédié. Le `<PodFrameColorsWhitelistSelector>` ci-dessus pose
            ce mapping automatiquement — l'éditeur ci-dessous permet de
            l'ajuster manuellement après coup. */}
        <PodMatrixDefaultsSelector />

        {/* Les 5 paramètres publication Etsy ci-dessous partagent une
            même nature (defaults appliqués au publish) : on les pose en
            grille auto-fit, chaque cellule ~600-700 px. Ça évite l'effet
            « 2 caractères de contenu + 1800 px de vide à droite » qu'on
            avait quand chaque bloc occupait toute la largeur de la page. */}
        <div className="printable-specs-etsy-defaults-grid">
          <EtsyShippingProfileSelector />
          <EtsyReadinessStateSelector />
          <EtsyReturnPolicySelector />
          <EtsyFreeShippingZonesSelector />
        </div>
      </section>

      {creating && (
        <PrintableSpecModal onClose={() => setCreating(false)} onSaved={() => setCreating(false)} />
      )}
    </section>
  )
}
